import Modal from "@/components/controls/Modal";
import Tabs from "@/components/controls/Tabs";
import Text from "@/components/ui/text";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MILESTONES,
  STAGES
} from "@/components/layouts/StageAverageDuration/StageAverageDuration";
import Dropdown from "@/components/controls/Dropdown/Dropdown";
import { FileDown } from "@dreamhub/designsystem/icons";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/utils/utils";
import UserBreadcrumbs from "@/components/controls/UserBreadcrumbs/UserBreadcrumbs";
import { useConversionBreadcrumbs } from "@/components/layouts/ConversionRates/ConversionBreadcrumbsContext";
import { ConversionRatesChartDataType } from "@/components/layouts/ConversionRates/ConversionRates";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import {
  periodsDropdownProps,
  renderMilestone
} from "@/components/layouts/ConversionRates/helpers";
import ConversionDrilldown from "./ConversionDrilldown";
import {
  periodEnumToString,
  periodStringToEnum
} from "@/entities/reporting/helpers";
import { SettingsPeriod } from "@/entities/reporting/types";
import FeedbackButton from "@/components/controls/FeedbackButton";
import { find, groupBy, isEmpty, map } from "lodash";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  chartData: {
    [key: string]: ConversionRatesChartDataType[];
  };
  mainSelectedStageId: string;
  mainSelectedTab: string;
  period: SettingsPeriod;
  periodSelect: (period: SettingsPeriod) => void;
}

const ConversionDrilldownModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  chartData = {},
  mainSelectedStageId,
  mainSelectedTab,
  period,
  periodSelect
}) => {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(STAGES);
  const [selectedStageId, setSelectedStageId] = useState("");
  const contentRef = useRef<HTMLDivElement>(null);
  const [minHeight, setMinHeight] = useState<number | undefined>(undefined);
  const isInitializedRef = useRef(false);

  const { breadcrumbIds, setSelectedUserId, dynamicTableData, isLoading } =
    useConversionBreadcrumbs();

  useEffect(() => {
    if (chartData && chartData[mainSelectedTab]) {
      // Only set the stage/tab on initial mount or when mainSelectedStageId/mainSelectedTab props change
      if (!isInitializedRef.current || mainSelectedStageId) {
        setSelectedStageId(
          isEmpty(mainSelectedStageId)
            ? chartData[mainSelectedTab][0]?.id || ""
            : mainSelectedStageId
        );
        setSelectedTab(mainSelectedTab);
        isInitializedRef.current = true;
      }
    }
  }, [mainSelectedStageId, mainSelectedTab, chartData]);

  useEffect(() => {
    if (chartData && chartData[selectedTab]) {
      if (!selectedStageId && chartData[selectedTab][0]) {
        // If no stage is selected, select the first one
        setSelectedStageId(chartData[selectedTab][0].id);
      } else if (selectedStageId) {
        // If a stage is selected, verify it still exists in the new data
        const stageExists = chartData[selectedTab].some(
          (element) => element.id === selectedStageId
        );
        if (!stageExists && chartData[selectedTab][0]) {
          setSelectedStageId(chartData[selectedTab][0].id);
        }
      }
    }
  }, [chartData, selectedTab, selectedStageId]);

  useEffect(() => {
    if (contentRef.current && !minHeight && !isLoading) {
      const height = contentRef.current.offsetHeight;
      if (height > 0) {
        setMinHeight(height);
      }
    }
  }, [dynamicTableData, isLoading, minHeight]);

  const stageGroups =
    chartData && chartData["stages"]
      ? groupBy(chartData["stages"], "milestone")
      : {};

  const renderStage = (stage: ConversionRatesChartDataType, index: number) => (
    <div
      className={cn(
        "p-3 flex gap-2 items-center",
        "transition-all duration-200 ease-in",
        selectedStageId === stage.id ? "bg-surface-elevation0" : ""
      )}
      key={stage.id}
    >
      <Avatar className="!size-5 !text-[10px] !text-on-surface">
        <AvatarFallback
          style={{
            backgroundColor: getColorForMilestoneByType(
              stage.milestone || undefined
            )
          }}
        >
          {index + 1}
        </AvatarFallback>
      </Avatar>
      <Text
        as="div"
        variant={selectedStageId === stage.id ? "label" : "bodySecondary"}
      >
        {stage.label}
      </Text>
    </div>
  );

  const selectedElement = find(
    chartData[selectedTab],
    (element) => element.id === selectedStageId
  );

  return (
    <Modal
      open={isOpen}
      setIsOpen={setIsOpen}
      contentClassname="w-[95%]"
      title={
        <div className="flex">
          <div className="flex flex-col gap-3 flex-1">
            <div className="flex flex-col flex-nowrap items-start justify-between align-start">
              <UserBreadcrumbs
                title={t("conversionRates.title")}
                subtitle={t("stageAverageDuration.byDirectReport")}
                ids={breadcrumbIds}
                setter={setSelectedUserId}
              />
            </div>
          </div>
          <div className="flex-0 justify-center">
            <Tabs
              options={{
                [STAGES]: t("stageAverageDuration.stages"),
                [MILESTONES]: t("stageAverageDuration.milestones")
              }}
              defaultValue={selectedTab}
              valueSelected={(value) => {
                setSelectedStageId(chartData[value][0].id);
                setSelectedTab(value);
              }}
            />
          </div>
          <div className="flex-1 flex justify-end items-start">
            <div className="flex gap-4 items-center text-h3">
              <Dropdown
                {...periodsDropdownProps(t)}
                defaultValue={periodEnumToString(period)}
                valueSelected={(value) =>
                  periodSelect(periodStringToEnum(value))
                }
              />
              <div className="flex gap-3">
                <div className="flex scale-150 items-center">
                  <FileDown />
                </div>
                <FeedbackButton
                  tooltip={t("feedback")}
                  idContext="conversion-drilldown"
                  variant="iconNoBg"
                />
              </div>
            </div>
          </div>
        </div>
      }
      content={
        <div
          ref={contentRef}
          className="flex h-full w-full"
          style={{ minHeight: minHeight ? `${minHeight}px` : "20rem" }}
        >
          <div className="flex flex-col min-w-72 w-fit">
            {map(chartData[selectedTab], (element, index) => (
              <div
                key={index}
                onClick={() => setSelectedStageId(element.id)}
                data-test={`${element.id}-element`}
                className={cn(
                  "w-full cursor-pointer border-border-border1 border-r",
                  selectedStageId === element.id &&
                    "border-t border-b border-r-0",
                  selectedStageId === element.id && index === 0 && "border-t-0"
                )}
              >
                {selectedTab === STAGES
                  ? renderStage(element, index)
                  : renderMilestone(t, stageGroups, element, index, "p-3")}
              </div>
            ))}
            <div className="h-full border-border-border1 border-r" />
          </div>
          <ConversionDrilldown
            selectedElement={selectedElement}
            selectedStageId={selectedStageId}
            selectedTab={selectedTab}
          />
        </div>
      }
    />
  );
};

export default ConversionDrilldownModal;
